import { useState } from "react";
import axios from "axios";

import API from "@/apiConfig";

/**
 * DisplayNameModal — shown on first login (when user.display_name is not set).
 * Pre-fills with the user's Google display name so they can confirm or change it.
 */
export default function DisplayNameModal({ defaultName = "", onSave }) {
  const [value, setValue] = useState(defaultName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) { setError("Please enter a display name."); return; }
    if (trimmed.length > 40) { setError("Max 40 characters."); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/me/profile`, { display_name: trimmed });
      onSave(trimmed);
    } catch {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="bg-[#0f1e35] border border-blue-900/40 rounded-2xl w-full max-w-sm p-7 shadow-2xl">
        <h2 className="text-xl font-bold text-white mb-1">Welcome!</h2>
        <p className="text-sm text-blue-300 mb-5">
          Choose a Display Name — this is what others see in Collectives, the directory, and prayer walls. Emails are always hidden.
        </p>

        <label className="block text-xs text-blue-400 uppercase tracking-widest mb-1.5">Display Name</label>
        <input
          data-testid="display-name-input"
          type="text"
          value={value}
          onChange={e => { setValue(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handleSave()}
          maxLength={40}
          placeholder="e.g. Sarah K."
          className="w-full bg-[#1a2d4a] border border-blue-800/50 text-white rounded-lg px-4 py-3 text-base focus:outline-none focus:border-blue-500 mb-1"
        />
        <div className="flex justify-between text-xs text-blue-500 mb-4">
          <span>{error && <span className="text-red-400">{error}</span>}</span>
          <span>{value.length}/40</span>
        </div>

        <button
          data-testid="display-name-save-btn"
          onClick={handleSave}
          disabled={saving || !value.trim()}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors"
        >
          {saving ? "Saving…" : "Save & Continue"}
        </button>
      </div>
    </div>
  );
}
